import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";

import Header from "./Front/Header";
import Footer from "./Front/Footer";
import API_BASE_URL from "../Config";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/thumbs";
import { toast } from "react-toastify";
import "./styles/DoctorRegister.css";
import "../styles/global.css";

const initialFormData = {
  name: "",
  mobile: "",
  email: "",
  password: "",
  terms: false,
};

const validators = {
  name: (v) => {
    if (!v || !v.trim()) return "Full name is required";
    if (v.trim().length < 2) return "Name must be at least 2 characters";
    if (!/^[a-zA-Z\s.'-]+$/.test(v.trim())) return "Name can only contain letters";
    return "";
  },
  email: (v) => {
    if (!v || !v.trim()) return "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())) return "Enter a valid email address";
    return "";
  },
  mobile: (v) => {
    if (!v || !v.trim()) return "Mobile number is required";
    const digits = v.replace(/\D/g, "");
    if (digits.length < 10) return "Mobile must be at least 10 digits";
    if (digits.length > 15) return "Mobile is too long";
    return "";
  },
  password: (v) => {
    if (!v) return "Password is required";
    if (v.length < 8) return "Password must be at least 8 characters";
    if (!/[A-Z]/.test(v)) return "Add at least one uppercase letter";
    if (!/[a-z]/.test(v)) return "Add at least one lowercase letter";
    if (!/[0-9]/.test(v)) return "Add at least one number";
    if (!/[^A-Za-z0-9]/.test(v)) return "Add at least one special character";
    return "";
  },
  terms: (v) => (!v ? "You must accept the doctor terms" : ""),
  otp: (v) => {
    if (!v || !v.trim()) return "Please enter the OTP";
    if (!/^\d{6}$/.test(v.trim())) return "OTP must be 6 digits";
    return "";
  },
};

const getPasswordStrength = (pw) => {
  if (!pw) return { score: 0, label: "", color: "" };
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  let label = "Weak"; let color = "weak";
  if (score >= 4) { label = "Strong"; color = "strong"; }
  else if (score >= 3) { label = "Good"; color = "good"; }
  else if (score >= 2) { label = "Medium"; color = "medium"; }
  return { score, label, color };
};

/* ═══════════════════════════════════════════════════════════
   SUCCESS SCREEN — rendered in the same file, no separate file
═══════════════════════════════════════════════════════════ */
const SUCCESS_CSS = `
  .suc-page {
    background: linear-gradient(135deg,#f0fdf8 0%,#e6f9f2 50%,#d1fae5 100%);
    min-height:100vh; display:flex; align-items:center;
    padding:48px 0 64px; position:relative; overflow:hidden;
  }
  .suc-page::before {
    content:''; position:absolute; top:-100px; right:-100px;
    width:360px; height:360px; border-radius:50%;
    background:radial-gradient(circle,rgba(16,185,129,.13) 0%,transparent 70%);
    pointer-events:none;
  }
  .suc-page::after {
    content:''; position:absolute; bottom:-120px; left:-120px;
    width:430px; height:430px; border-radius:50%;
    background:radial-gradient(circle,rgba(13,148,136,.10) 0%,transparent 70%);
    pointer-events:none;
  }

  /* Card */
  .suc-card {
    background:#fff; border-radius:24px; position:relative; z-index:1;
    box-shadow:0 24px 80px -16px rgba(16,185,129,.18),0 4px 20px rgba(0,0,0,.06);
    border:1px solid rgba(16,185,129,.10); overflow:hidden;
  }
  .suc-top-bar { height:6px; background:linear-gradient(90deg,#10b981 0%,#0d9488 50%,#0e9090 100%); }
  .suc-body { padding:48px 44px 42px; text-align:center; }

  /* Animated checkmark */
  .suc-icon-wrap { position:relative; width:100px; height:100px; margin:0 auto 26px; }
  .suc-ring {
    position:absolute; inset:0; border-radius:50%;
    border:3px solid rgba(16,185,129,.22);
    animation:sucPulse 2.6s ease-out infinite;
  }
  .suc-ring-2 { animation-delay:.85s; }
  @keyframes sucPulse {
    0%   { transform:scale(1); opacity:.65; }
    100% { transform:scale(1.55); opacity:0; }
  }
  .suc-circle {
    position:absolute; inset:8px; border-radius:50%;
    background:linear-gradient(135deg,#10b981 0%,#059669 100%);
    display:flex; align-items:center; justify-content:center;
    box-shadow:0 10px 32px -8px rgba(16,185,129,.52);
    animation:sucPop .5s cubic-bezier(.175,.885,.32,1.275) forwards;
  }
  @keyframes sucPop {
    0%  { transform:scale(0); opacity:0; }
    80% { transform:scale(1.1); }
    100%{ transform:scale(1); opacity:1; }
  }
  .suc-check {
    stroke-dasharray:40; stroke-dashoffset:0;
    animation:sucDraw .4s ease .35s both;
  }
  @keyframes sucDraw {
    from { stroke-dashoffset:40; opacity:0; }
    to   { stroke-dashoffset:0;  opacity:1; }
  }

  /* Badge */
  .suc-badge {
    display:inline-flex; align-items:center; gap:6px;
    background:rgba(16,185,129,.10); color:#059669;
    font-size:11.5px; font-weight:700; text-transform:uppercase;
    letter-spacing:.08em; padding:5px 14px; border-radius:999px;
    border:1px solid rgba(16,185,129,.26); margin-bottom:14px;
  }
  .suc-dot {
    width:7px; height:7px; border-radius:50%; background:#10b981;
    animation:sucBlink 1.4s ease-in-out infinite;
  }
  @keyframes sucBlink { 0%,100%{opacity:1} 50%{opacity:.3} }

  .suc-title { font-size:1.65rem; font-weight:700; color:#0f172a; margin:0 0 16px; line-height:1.25; }
  .suc-title span {
    background:linear-gradient(90deg,#10b981,#0d9488);
    -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text;
  }

  /* Email chip */
  .suc-email {
    display:inline-flex; align-items:center; gap:8px;
    background:#f8fafc; border:1.5px solid #e2e8f0; border-radius:10px;
    padding:9px 16px; font-size:.875rem; color:#334155; font-weight:500;
    margin-bottom:22px;
  }
  .suc-email svg { color:#0d9488; flex-shrink:0; }

  /* Message box */
  .suc-msg {
    background:linear-gradient(135deg,#f0fdf9,#ecfdf5);
    border:1px solid #a7f3d0; border-radius:14px;
    padding:18px 20px; margin-bottom:22px; text-align:left;
  }
  .suc-msg p { font-size:.9rem; color:#065f46; line-height:1.65; margin:0; }

  /* Steps strip */
  .suc-steps {
    display:flex; gap:0; margin-bottom:22px;
    border-radius:12px; overflow:hidden; border:1px solid #e2e8f0;
  }
  .suc-step {
    flex:1; padding:13px 8px; text-align:center;
    background:#f8fafc; border-right:1px solid #e2e8f0;
  }
  .suc-step:last-child { border-right:none; }
  .suc-step-num {
    width:26px; height:26px; border-radius:50%;
    background:linear-gradient(135deg,#10b981,#0d9488);
    color:#fff; font-size:12px; font-weight:700;
    display:flex; align-items:center; justify-content:center; margin:0 auto 6px;
  }
  .suc-step-txt { font-size:11.5px; color:#475569; font-weight:500; line-height:1.4; }

  /* Spam note */
  .suc-spam {
    display:flex; align-items:flex-start; gap:10px;
    background:#fffbeb; border:1px solid #fde68a; border-radius:10px;
    padding:11px 15px; text-align:left; margin-bottom:24px;
  }
  .suc-spam svg { flex-shrink:0; color:#d97706; margin-top:1px; }
  .suc-spam p { font-size:12.5px; color:#92400e; margin:0; line-height:1.5; }

  /* Buttons */
  .suc-actions { display:flex; gap:12px; justify-content:center; flex-wrap:wrap; }
  .suc-btn-p {
    display:inline-flex; align-items:center; gap:8px;
    padding:12px 26px; border-radius:10px; font-size:.9rem; font-weight:600;
    color:#fff; background:linear-gradient(135deg,#10b981 0%,#0d9488 100%);
    border:none; cursor:pointer; text-decoration:none;
    transition:transform .15s,box-shadow .15s,opacity .15s;
    box-shadow:0 6px 18px -4px rgba(16,185,129,.45);
  }
  .suc-btn-p:hover { transform:translateY(-1px); box-shadow:0 10px 24px -4px rgba(16,185,129,.55); color:#fff; opacity:.94; }
  .suc-btn-s {
    display:inline-flex; align-items:center; gap:8px;
    padding:12px 26px; border-radius:10px; font-size:.9rem; font-weight:600;
    color:#475569; background:#fff; border:1.5px solid #e2e8f0;
    cursor:pointer; text-decoration:none;
    transition:border-color .15s,background .15s,color .15s;
  }
  .suc-btn-s:hover { border-color:#0d9488; color:#0d9488; background:#f0fdf9; }

  /* Countdown */
  .suc-cd {
    margin-top:20px; font-size:13px; color:#94a3b8;
    display:flex; align-items:center; justify-content:center; gap:8px;
  }
  .suc-cd-badge {
    background:#f1f5f9; border-radius:6px; padding:2px 9px;
    font-weight:700; color:#475569; min-width:26px; text-align:center;
    font-variant-numeric:tabular-nums;
  }

  /* Responsive */
  @media(max-width:576px){
    .suc-body { padding:34px 20px 30px; }
    .suc-title { font-size:1.3rem; }
    .suc-actions { flex-direction:column; }
    .suc-btn-p,.suc-btn-s { width:100%; justify-content:center; }
    .suc-steps { flex-direction:column; }
    .suc-step { border-right:none; border-bottom:1px solid #e2e8f0; }
    .suc-step:last-child { border-bottom:none; }
  }
`;

const RegistrationSuccessScreen = ({ registeredEmail, navigate }) => {
  const [countdown, setCountdown] = useState(10);

  /*useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) { clearInterval(timer); navigate("/login"); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [navigate]);*/

  return (
    <>
      <style>{SUCCESS_CSS}</style>
      <Header />
      <section className="suc-page">
        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <div className="row justify-content-center">
            <div className="col-12 col-sm-10 col-md-8 col-lg-6 col-xl-5">
              <div className="suc-card">
                <div className="suc-top-bar" />
                <div className="suc-body">

                  {/* Animated icon */}
                  <div className="suc-icon-wrap">
                    <div className="suc-ring" />
                    <div className="suc-ring suc-ring-2" />
                    <div className="suc-circle">
                      <svg className="suc-check" width="38" height="38" viewBox="0 0 24 24"
                        fill="none" stroke="#fff" strokeWidth="2.8"
                        strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                  </div>

                  {/* Badge */}
                  <div className="suc-badge"><span className="suc-dot" /> Registration Complete</div>

                  {/* Title */}
                  <h2 className="suc-title">
                    You have <span>successfully registered!</span>
                  </h2>

                  {/* Email chip */}
                  {registeredEmail && (
                    <div className="suc-email">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                      {registeredEmail}
                    </div>
                  )}

                  {/* Main message */}
                  <div className="suc-msg">
                    <p>
                      You have successfully registered as a doctor. You will receive an email to
                      complete your profile and onboard as a doctor. Please check your inbox.
                      If you don't see the email, kindly check your <strong>spam folder</strong>.
                    </p>
                  </div>

                  {/* Next steps */}
                  <div className="suc-steps">
                    <div className="suc-step">
                      <div className="suc-step-num">1</div>
                      <div className="suc-step-txt">Check your email inbox</div>
                    </div>
                    <div className="suc-step">
                      <div className="suc-step-num">2</div>
                      <div className="suc-step-txt">Complete your profile</div>
                    </div>
                    <div className="suc-step">
                      <div className="suc-step-num">3</div>
                      <div className="suc-step-txt">Go live & see patients</div>
                    </div>
                  </div>

                  {/* Spam note */}
                  <div className="suc-spam">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                    <p>
                      <strong>Can't find the email?</strong> Verification emails sometimes land in spam or promotions.
                      Check all folders and mark us as a safe sender.
                    </p>
                  </div>

                  {/* Buttons */}
                  <div className="suc-actions">
                   <a
  href="https://aceveda.com/doctoradmin/#/login"
  className="suc-btn-p"
  target="_blank"
  rel="noopener noreferrer"
>
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
    <polyline points="10 17 15 12 10 7" />
    <line x1="15" y1="12" x2="3" y2="12" />
  </svg>

  Go to Login
</a>
                    <Link to="/" className="suc-btn-s">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                        <polyline points="9 22 9 12 15 12 15 22"/>
                      </svg>
                      Back to Home
                    </Link>
                  </div>

                  

                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN EXPORT
═══════════════════════════════════════════════════════════ */
const DoctorRegister = () => {
  const [registerErrors, setRegisterErrors] = useState({});
  const [otpError, setOtpError] = useState("");
  const [otpInput, setOtpInput] = useState("");

  const [showOtpLoginModal, setShowOtpLoginModal] = useState(false);
  const [otpLoginStep, setOtpLoginStep] = useState("mobile");
  const [otpLoginMobile, setOtpLoginMobile] = useState("");
  const [otpLoginCode, setOtpLoginCode] = useState("");
  const [otpLoginErrors, setOtpLoginErrors] = useState({});
  const [otpLoginSending, setOtpLoginSending] = useState(false);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const navigate = useNavigate();

  // ── NEW: controls success screen ──────────────────
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  // ──────────────────────────────────────────────────

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/login/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
        mode: "cors",
      });
      const data = await res.json();
      if (res.status) {
        localStorage.setItem("authToken", data.token);
        localStorage.setItem("config_id", data.config_id);
        localStorage.setItem("user_id", data.user.id);
        localStorage.setItem("user", JSON.stringify(data.user));
        localStorage.setItem("name", data.user.name);
        localStorage.setItem("role", data.user.role);
        setResponseMessage("Login successful!");
        navigate("/");
      } else {
        let errorMessage = "Something Went Wrong.";
        if (res.status === 400) errorMessage = "Bad request. Please check the input data.";
        else if (res.status === 401) errorMessage = "Unauthorized. Incorrect username or password.";
        else if (res.status === 403) errorMessage = "Forbidden. You don't have permission to access this.";
        else if (res.status === 500) errorMessage = "Server error. Please try again later.";
        setResponseMessage(errorMessage);
      }
    } catch (error) {
      console.error("Error:", error);
      setResponseMessage("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const [formRegisterData, setFormRegisterData] = useState(initialFormData);

  const handleRegisterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormRegisterData({ ...formRegisterData, [name]: type === "checkbox" ? checked : value });
    if (registerErrors[name]) setRegisterErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {
      name: validators.name(formRegisterData.name),
      email: validators.email(formRegisterData.email),
      mobile: validators.mobile(formRegisterData.mobile),
      password: validators.password(formRegisterData.password),
      terms: validators.terms(formRegisterData.terms),
    };
    setRegisterErrors(newErrors);
    if (Object.values(newErrors).some((v) => v)) { toast.error("Please fix the errors below"); return; }
    setShowOtp(true);
  };

  const [showOtp, setShowOtp] = useState(false);

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const err = validators.otp(otpInput);
    if (err) { setOtpError(err); return; }
    setOtpError("");

    try {
      const response = await fetch(`${API_BASE_URL}api/doctorRegister`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formRegisterData),
      });
      if (!response.ok) { const text = await response.text(); console.error("Server error:", text); throw new Error("API Error"); }
      const data = await response.json();

      if (data.status) {
        // ── Show success screen ─────────────────────
        setRegisteredEmail(formRegisterData.email);
        setRegistrationSuccess(true);
        // ────────────────────────────────────────────
        setFormRegisterData(initialFormData);
        setOtpInput("");
        setShowOtp(false);
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      console.error("Fetch failed:", err);
      toast.error("Fetch failed – check API URL / CORS");
    }
  };

  const handleGoogleSignup = () => { toast.info("Google sign-up will be available soon"); };

  const openOtpLogin = () => { setShowOtpLoginModal(true); setOtpLoginStep("mobile"); setOtpLoginMobile(""); setOtpLoginCode(""); setOtpLoginErrors({}); };
  const closeOtpLogin = () => { setShowOtpLoginModal(false); setOtpLoginStep("mobile"); setOtpLoginMobile(""); setOtpLoginCode(""); setOtpLoginErrors({}); };

  const handleOtpLoginSendCode = (e) => {
    e.preventDefault();
    const err = validators.mobile(otpLoginMobile);
    if (err) { setOtpLoginErrors({ mobile: err }); return; }
    setOtpLoginErrors({}); setOtpLoginSending(true);
    setTimeout(() => { setOtpLoginSending(false); setOtpLoginStep("verify"); toast.success(`OTP sent to ${otpLoginMobile}`); }, 800);
  };

  const handleOtpLoginVerify = (e) => {
    e.preventDefault();
    const err = validators.otp(otpLoginCode);
    if (err) { setOtpLoginErrors({ code: err }); return; }
    setOtpLoginErrors({}); setOtpLoginSending(true);
    setTimeout(() => { setOtpLoginSending(false); toast.success("Logged in successfully"); closeOtpLogin(); }, 800);
  };

  const handleOtpLoginResend = () => { toast.success(`New OTP sent to ${otpLoginMobile}`); };

  const pwStrength = getPasswordStrength(formRegisterData.password);

  // ── Render success screen conditionally ──────────
  if (registrationSuccess) {
    return <RegistrationSuccessScreen registeredEmail={registeredEmail} navigate={navigate} />;
  }
  // ─────────────────────────────────────────────────

  return (
    <>
      <Header />
      <section className="doc-page">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 col-lg-11 col-xl-10">
              <div className="doc-card">
                {loading && <div className="doc-loader"><div className="doc-spinner"></div></div>}
                <div className="doc-grid">

                  {/* ═══ LEFT PANEL ═══ */}
                  <div className="doc-visual">
                    <div className="doc-brand">
                      <div className="doc-brand-mark">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7Z" />
                        </svg>
                      </div>
                      ACEVEDA <span className="doc-brand-tag">For Doctors</span>
                    </div>
                    <div className="doc-visual-content">
                      <h1>Join our <span>medical network.</span></h1>
                      <p>Connect with patients, manage appointments digitally, and grow your practice with our trusted healthcare platform.</p>
                      <div className="doc-benefits">
                        <div className="doc-benefit"><div className="doc-benefit-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></div><div><div className="doc-benefit-text">Reach More Patients</div><div className="doc-benefit-sub">Expand your practice digitally</div></div></div>
                        <div className="doc-benefit"><div className="doc-benefit-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></div><div><div className="doc-benefit-text">Smart Scheduling</div><div className="doc-benefit-sub">Set your own availability</div></div></div>
                        <div className="doc-benefit"><div className="doc-benefit-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg></div><div><div className="doc-benefit-text">Easy Payments</div><div className="doc-benefit-sub">Get paid instantly online</div></div></div>
                      </div>
                    </div>
                    <div className="doc-stats">
                      <div><p className="doc-stat-num">500+</p><p className="doc-stat-lbl">Doctors</p></div>
                      <div><p className="doc-stat-num">50K+</p><p className="doc-stat-lbl">Patients</p></div>
                      <div><p className="doc-stat-num">24/7</p><p className="doc-stat-lbl">Support</p></div>
                    </div>
                  </div>

                  {/* ═══ RIGHT PANEL ═══ */}
                  <div className="doc-form-wrap">
                    <div className="doc-steps">
                      <div className={`doc-step ${!showOtp ? "is-active" : "is-done"}`}>
                        <div className="doc-step-circle">{showOtp ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg> : "1"}</div>
                        <span className="doc-step-label">Details</span>
                      </div>
                      <div className={`doc-step-line ${showOtp ? "is-done" : ""}`}></div>
                      <div className={`doc-step ${showOtp ? "is-active" : ""}`}>
                        <div className="doc-step-circle">2</div>
                        <span className="doc-step-label">Verify</span>
                      </div>
                    </div>

                    {!showOtp && (
                      <>
                        <h2 className="doc-title">Doctor Registration</h2>
                        <p className="doc-subtitle">Create your professional account in just two steps</p>
                        <div className="doc-patient-link"><span>👤 Are you a Patient?</span><Link to="/login-register">Login or Register here →</Link></div>
                        <div className="doc-verified-banner">
                          <div className="doc-verified-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12l2 2 4-4"/><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></div>
                          <div className="doc-verified-text"><p className="doc-verified-title">Verified Doctor Network</p><p className="doc-verified-sub">All accounts are reviewed within 24 hours of registration</p></div>
                        </div>
                        <form onSubmit={handleRegisterSubmit} noValidate>
                          {/* Full Name */}
                          <div className="doc-field">
                            <label className="doc-label">Full Name</label>
                            <div className="doc-input-wrap">
                              <input type="text" className={`doc-input ${registerErrors.name?"has-error":""}`} name="name" placeholder="Dr. John Doe" value={formRegisterData.name} onChange={handleRegisterChange}/>
                              <span className="doc-input-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></span>
                            </div>
                            {registerErrors.name && <div className="doc-error"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>{registerErrors.name}</div>}
                          </div>
                          {/* Email */}
                          <div className="doc-field">
                            <label className="doc-label">Email Address</label>
                            <div className="doc-input-wrap">
                              <input type="email" className={`doc-input ${registerErrors.email?"has-error":""}`} name="email" placeholder="doctor@clinic.com" value={formRegisterData.email} onChange={handleRegisterChange}/>
                              <span className="doc-input-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg></span>
                            </div>
                            {registerErrors.email && <div className="doc-error"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>{registerErrors.email}</div>}
                          </div>
                          {/* Mobile */}
                          <div className="doc-field">
                            <label className="doc-label">Mobile Number</label>
                            <div className="doc-input-wrap">
                              <input type="text" className={`doc-input ${registerErrors.mobile?"has-error":""}`} name="mobile" placeholder="+91 00000 00000" value={formRegisterData.mobile} onChange={handleRegisterChange}/>
                              <span className="doc-input-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg></span>
                            </div>
                            {registerErrors.mobile && <div className="doc-error"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>{registerErrors.mobile}</div>}
                          </div>
                          {/* Password */}
                          <div className="doc-field">
                            <label className="doc-label">Create Password</label>
                            <div className="doc-input-wrap">
                              <input type={showPassword?"text":"password"} className={`doc-input ${registerErrors.password?"has-error":""}`} name="password" placeholder="Minimum 8 characters" value={formRegisterData.password} onChange={handleRegisterChange}/>
                              <span className="doc-input-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></span>
                              <button type="button" className="doc-pw-toggle" onClick={()=>setShowPassword(!showPassword)}>
                                {showPassword
                                  ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                                  : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                }
                              </button>
                            </div>
                            {formRegisterData.password && (
                              <div className="doc-pw-strength-wrap">
                                <div className="doc-pw-strength">
                                  {[1,2,3,4,5].map(i=><div key={i} className={`doc-pw-bar ${pwStrength.score>=i?`is-${pwStrength.color}`:""}`}></div>)}
                                </div>
                                <div className="doc-pw-meta"><span style={{color:"var(--doc-muted)"}}>Password strength:</span><span className={`doc-pw-label is-${pwStrength.color}`}>{pwStrength.label}</span></div>
                                <div className="doc-pw-checks">
                                  {[
                                    [formRegisterData.password.length>=8,"8+ characters"],
                                    [/[A-Z]/.test(formRegisterData.password),"Uppercase letter"],
                                    [/[a-z]/.test(formRegisterData.password),"Lowercase letter"],
                                    [/[0-9]/.test(formRegisterData.password),"Number"],
                                    [/[^A-Za-z0-9]/.test(formRegisterData.password),"Special character"],
                                  ].map(([met,label],i)=>(
                                    <span key={i} className={`doc-pw-check ${met?"is-met":""}`}>
                                      <span className="doc-pw-check-dot">{met&&<svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}</span>
                                      {label}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {registerErrors.password && <div className="doc-error"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>{registerErrors.password}</div>}
                          </div>
                          {/* Terms */}
                          <label className="doc-terms">
                            <input type="checkbox" name="terms" checked={formRegisterData.terms} onChange={handleRegisterChange}/>
                            <span>I confirm I'm a licensed medical practitioner and agree to the <a href="#">Doctor Terms</a>, <a href="#">Code of Conduct</a> and <a href="#">Privacy Policy</a>.</span>
                          </label>
                          {registerErrors.terms && <div className="doc-error" style={{marginTop:-4,marginBottom:14}}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>{registerErrors.terms}</div>}
                          {/* OTP login trigger */}
                          <div className="doc-otp-note" onClick={openOtpLogin} role="button">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
                            <div className="doc-otp-note-text"><p className="doc-otp-note-title">Already registered? Login with OTP</p><p className="doc-otp-note-sub">No password needed — get a code on your mobile</p></div>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                          </div>
                          <button type="submit" className="doc-btn doc-btn--primary">Continue to Verification <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg></button>
                        </form>
                        <div className="doc-divider">or sign up with</div>
                        <button type="button" className="doc-btn doc-btn--google" onClick={handleGoogleSignup}>
                          <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/><path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/></svg>
                          Continue with Google
                        </button>
                        <p className="doc-foot-note">Already a registered doctor? <Link to="https://aceveda.com/doctoradmin/">Doctor Login →</Link></p>
                      </>
                    )}

                    {showOtp && (
                      <>
                        <div className="doc-otp-head">
                          <div className="doc-otp-icon"><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="3"/><path d="M7 9l5 4 5-4"/></svg></div>
                          <h3 className="doc-otp-title">Verify your number</h3>
                          <p className="doc-otp-sub">We sent a 6-digit code to <b>{formRegisterData.mobile||"your mobile"}</b><button type="button" className="doc-otp-edit" onClick={()=>{setShowOtp(false);setOtpInput("");setOtpError("");}}>Edit</button></p>
                        </div>
                        <form onSubmit={handleOtpSubmit} noValidate>
                          <div className="doc-field">
                            <label className="doc-label">Enter OTP</label>
                            <div className="doc-input-wrap">
                              <input type="text" inputMode="numeric" className={`doc-input ${otpError?"has-error":""}`} placeholder="• • • • • •" maxLength={6} value={otpInput} onChange={(e)=>{const v=e.target.value.replace(/\D/g,"");setOtpInput(v);if(otpError)setOtpError("");}} style={{letterSpacing:"0.4em",textAlign:"center",fontWeight:700,fontSize:18,paddingLeft:14}}/>
                            </div>
                            {otpError && <div className="doc-error"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>{otpError}</div>}
                          </div>
                          <div className="doc-otp-resend"><span>Didn't receive code?<button type="button" className="doc-otp-link">Get via call</button></span><button type="button" className="doc-otp-link">Resend OTP</button></div>
                          <button type="submit" className="doc-btn doc-btn--primary">Complete Registration <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></button>
                        </form>
                        <p className="doc-foot-note"><button onClick={()=>{setShowOtp(false);setOtpInput("");setOtpError("");}}>← Back to registration</button></p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ OTP LOGIN MODAL ═══ */}
      {showOtpLoginModal && (
        <div className="doc-modal-bg" onClick={closeOtpLogin}>
          <div className="doc-modal" onClick={(e)=>e.stopPropagation()}>
            <button type="button" className="doc-modal-close" onClick={closeOtpLogin}>✕</button>
            <div className="doc-modal-icon">
              {otpLoginStep==="mobile"
                ? <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
                : <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12l2 2 4-4"/><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              }
            </div>
            {otpLoginStep==="mobile" && (
              <>
                <h4>Login with OTP</h4>
                <p className="doc-modal-desc">Enter your registered mobile number and we'll send a verification code.</p>
                <form onSubmit={handleOtpLoginSendCode} noValidate>
                  <div className="doc-field">
                    <label className="doc-label">Mobile Number</label>
                    <div className="doc-input-wrap">
                      <input type="tel" inputMode="numeric" className={`doc-input ${otpLoginErrors.mobile?"has-error":""}`} placeholder="+91 00000 00000" value={otpLoginMobile} onChange={(e)=>{setOtpLoginMobile(e.target.value);if(otpLoginErrors.mobile)setOtpLoginErrors({});}} autoFocus/>
                      <span className="doc-input-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg></span>
                    </div>
                    {otpLoginErrors.mobile && <div className="doc-error"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>{otpLoginErrors.mobile}</div>}
                  </div>
                  <div className="doc-modal-actions"><button type="button" className="doc-btn doc-btn--ghost" onClick={closeOtpLogin}>Cancel</button><button type="submit" className="doc-btn doc-btn--primary" disabled={otpLoginSending}>{otpLoginSending?"Sending...":"Send OTP"}</button></div>
                </form>
              </>
            )}
            {otpLoginStep==="verify" && (
              <>
                <h4>Enter verification code</h4>
                <p className="doc-modal-desc">We sent a 6-digit code to <b>{otpLoginMobile}</b><button type="button" className="doc-otp-edit" onClick={()=>setOtpLoginStep("mobile")}>Change</button></p>
                <form onSubmit={handleOtpLoginVerify} noValidate>
                  <div className="doc-field">
                    <label className="doc-label">OTP Code</label>
                    <div className="doc-input-wrap">
                      <input type="text" inputMode="numeric" className={`doc-input ${otpLoginErrors.code?"has-error":""}`} placeholder="• • • • • •" maxLength={6} value={otpLoginCode} onChange={(e)=>{const v=e.target.value.replace(/\D/g,"");setOtpLoginCode(v);if(otpLoginErrors.code)setOtpLoginErrors({});}} autoFocus style={{letterSpacing:"0.4em",textAlign:"center",fontWeight:700,fontSize:18,paddingLeft:14}}/>
                    </div>
                    {otpLoginErrors.code && <div className="doc-error"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>{otpLoginErrors.code}</div>}
                  </div>
                  <div className="doc-otp-resend"><span>Didn't receive code?</span><button type="button" className="doc-otp-link" onClick={handleOtpLoginResend}>Resend OTP</button></div>
                  <div className="doc-modal-actions"><button type="button" className="doc-btn doc-btn--ghost" onClick={()=>setOtpLoginStep("mobile")}>Back</button><button type="submit" className="doc-btn doc-btn--primary" disabled={otpLoginSending}>{otpLoginSending?"Verifying...":"Verify & Login"}</button></div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default DoctorRegister;